/**
 * MessageBubble - Renders a single message in the Travel Agent chat
 *
 * Features:
 * - User messages: right-aligned, themed bubble
 * - Agent messages: left-aligned, grey/white bubble
 * - Parses [[ARTIFACT:TYPE:ID:LABEL]] patterns in agent content → ArtifactChip
 * - Parses [[PROPOSAL:#A12:label]] patterns → ProposalCard (below the bubble)
 * - Parses [[IMPORT_PREVIEW:batchId:title:count]] patterns → ImportPreviewCard (Phase 3b-3)
 * - Renders basic markdown (line breaks, **bold**)
 */

import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';
import { WaitingIndicator } from '@trip-planner-monorepo/shared';
import ArtifactChip from './ArtifactChip';
import ProposalCard from '../ProposalCard/ProposalCard';
import ImportPreviewCard from '../ImportPreviewCard/ImportPreviewCard';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(timestamp) {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Parse text that may contain [[ARTIFACT:TYPE:ID:LABEL]] tokens.
 * Returns an array of strings and ArtifactChip elements.
 *
 * @param {string} text
 * @param {Function} onArtifactClick
 * @returns {Array<string|React.Element>}
 */
function parseArtifacts(text, onArtifactClick) {
  if (!text) return [''];

  const ARTIFACT_PATTERN = /\[\[ARTIFACT:([^:\]]+):([^:\]]+):([^\]]+)\]\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = ARTIFACT_PATTERN.exec(text)) !== null) {
    const [fullMatch, type, id, label] = match;

    // Add text before this artifact
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the ArtifactChip element
    parts.push(
      <ArtifactChip
        key={`artifact-${id}-${match.index}`}
        type={type}
        id={id}
        label={label}
        onClick={onArtifactClick}
      />
    );

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function sanitizeTextWithoutArtifactTokens(text) {
  if (!text) return '';
  const ARTIFACT_PATTERN = /\[\[ARTIFACT:([^:\]]+):([^:\]]+):([^\]]+)\]\]/g;
  const PROPOSAL_PATTERN = /\[\[PROPOSAL:#(A\d+):([^\]]+)\]\]/g;
  const IMPORT_PREVIEW_PATTERN = /\[\[IMPORT_PREVIEW:[^\]]+\]\]/g;
  return String(text)
    // Replace each artifact token with just its label (the display name), keeping the text readable
    .replace(ARTIFACT_PATTERN, (_match, _type, _id, label) => label)
    // Remove proposal tokens entirely — they are rendered as ProposalCard components below the bubble
    .replace(PROPOSAL_PATTERN, '')
    // Remove import preview tokens — they are rendered as ImportPreviewCard components below the bubble
    .replace(IMPORT_PREVIEW_PATTERN, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// Proposal token parsing
// ---------------------------------------------------------------------------

const PROPOSAL_TOKEN_PATTERN = /\[\[PROPOSAL:#(A\d+):([^\]]+)\]\]/g;

/**
 * Parse [[PROPOSAL:#A12:label]] tokens from message text.
 * Returns an array of { refId, label } objects.
 *
 * @param {string} text
 * @returns {Array<{refId: string, label: string}>}
 */
function parseProposals(text) {
  if (!text) return [];
  const proposals = [];
  PROPOSAL_TOKEN_PATTERN.lastIndex = 0;
  let match;
  while ((match = PROPOSAL_TOKEN_PATTERN.exec(text)) !== null) {
    proposals.push({ refId: match[1], label: match[2] });
  }
  PROPOSAL_TOKEN_PATTERN.lastIndex = 0;
  return proposals;
}

// ---------------------------------------------------------------------------
// Import preview token parsing (Phase 3b-3)
// ---------------------------------------------------------------------------

/**
 * Parse [[IMPORT_PREVIEW:batchId:title:count]] token from message text.
 * Returns { batchId, title, count } if found, null otherwise.
 *
 * Token format: [[IMPORT_PREVIEW:batch_1234_abc:Hidden Italy Walk:8 items]]
 *
 * @param {string} text
 * @returns {{ batchId: string, title: string, count: string }|null}
 */
function parseImportPreview(text) {
  if (!text) return null;
  // Match the token: [[IMPORT_PREVIEW:batchId:title:count]]
  // batchId: no colons (alphanumeric + _ + -)
  // title:   may contain spaces and colons (lazy match stops at last :count)
  // count:   "N items" at the end
  const match = text.match(/\[\[IMPORT_PREVIEW:([^:\]]+):(.+?):(\d+[^\]]*)\]\]/);
  if (!match) return null;
  return {
    batchId: match[1].trim(),
    title: match[2].trim(),
    count: match[3].trim()
  };
}

function renderArtifactChipsFromMetadata(artifacts = [], onArtifactClick) {
  if (!Array.isArray(artifacts) || artifacts.length === 0) return [];
  return artifacts
    .filter((artifact) => artifact?.type && artifact?.id && artifact?.label)
    .map((artifact, index) => (
      <ArtifactChip
        key={`artifact-meta-${artifact.type}-${artifact.id}-${index}`}
        type={artifact.type}
        id={artifact.id}
        label={artifact.label}
        onClick={onArtifactClick}
      />
    ));
}

/**
 * Render a text segment with basic markdown-lite formatting.
 * Supports **bold** and newlines.
 *
 * @param {string} text
 * @param {number} keyBase
 * @returns {Array<React.Element>}
 */
function renderTextWithMarkdown(text, keyBase = 0) {
  if (!text) return [];

  const lines = text.split('\n');
  const result = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      result.push(<br key={`br-${keyBase}-${lineIndex}`} />);
    }

    // Parse **bold** within each line
    const boldPattern = /\*\*([^*]+)\*\*/g;
    const lineParts = [];
    let lastIdx = 0;
    let boldMatch;

    while ((boldMatch = boldPattern.exec(line)) !== null) {
      if (boldMatch.index > lastIdx) {
        lineParts.push(line.slice(lastIdx, boldMatch.index));
      }
      lineParts.push(
        <strong key={`bold-${keyBase}-${lineIndex}-${boldMatch.index}`}>
          {boldMatch[1]}
        </strong>
      );
      lastIdx = boldMatch.index + boldMatch[0].length;
    }

    if (lastIdx < line.length) {
      lineParts.push(line.slice(lastIdx));
    }

    result.push(...lineParts);
  });

  return result;
}

/**
 * Helper: Navigation hint shown above artifact chips that navigate away from this page.
 */
const NAV_HINT_TEXT =
  "The following will load the relevant page in the itineri app. " +
  "Please use the browser's back button if you want to return to this 'My Travel Agent' page.";

function NavHint({ keyProp }) {
  return (
    <Typography
      key={keyProp}
      variant="caption"
      component="div"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontStyle: 'italic',
        mt: 1,
        mb: 0.5,
        lineHeight: 1.4
      }}
    >
      {NAV_HINT_TEXT}
    </Typography>
  );
}

/**
 * Render message content, parsing artifacts and markdown.
 *
 * @param {string} content
 * @param {Function} onArtifactClick
 * @param {Array} artifacts
 */
function renderContent(content, onArtifactClick, artifacts = []) {
  // Strip proposal and import_preview tokens from content before rendering —
  // they are displayed as separate components below the bubble, not inline in the text.
  // IMPORTANT: collapse resulting blank lines — a 36-item import batch leaves 36+ consecutive
  // newlines after stripping, each rendered as <br />, causing a large blank space.
  const contentWithoutProposals = content
    ? String(content)
        .replace(/\[\[PROPOSAL:#(?:A\d+):(?:[^\]]+)\]\]/g, '')
        .replace(/\[\[IMPORT_PREVIEW:[^\]]+\]\]/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    : content;

  const metadataChips = renderArtifactChipsFromMetadata(artifacts, onArtifactClick);
  if (metadataChips.length > 0) {
    const cleanText = sanitizeTextWithoutArtifactTokens(contentWithoutProposals);
    return (
      <>
        {cleanText ? renderTextWithMarkdown(cleanText, 0) : null}
        {cleanText ? <br /> : null}
        <NavHint keyProp="nav-hint-meta" />
        {metadataChips}
      </>
    );
  }

  const parts = parseArtifacts(contentWithoutProposals, onArtifactClick);

  // Insert nav hint just before the first ArtifactChip element
  const firstChipIndex = parts.findIndex((part) => typeof part !== 'string');
  if (firstChipIndex !== -1) {
    parts.splice(firstChipIndex, 0, <NavHint key="nav-hint-inline" />);
  }

  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return (
        <React.Fragment key={`text-${index}`}>
          {renderTextWithMarkdown(part, index)}
        </React.Fragment>
      );
    }
    // Already a React element (ArtifactChip or NavHint)
    return part;
  });
}

// ---------------------------------------------------------------------------
// MessageBubble Component
// ---------------------------------------------------------------------------

/**
 * MessageBubble
 *
 * @param {Object} props
 * @param {Object} props.message - Message data { id, role, content, timestamp, metadata }
 * @param {Function} props.onArtifactClick - Called when an artifact chip is clicked
 * @param {boolean} [props.isStreaming=false] - Show loading indicator
 * @param {Array}  [props.suggestions=[]] - All outstanding/resolved suggestion docs
 * @param {Function} [props.onApprove] - (suggestionId) => void
 * @param {Function} [props.onDismiss] - (suggestionId) => void
 * @param {Function} [props.onModify] - (suggestionId, note) => void
 * @param {boolean} [props.canApprove=true] - Whether the current user can approve proposals
 */
const MessageBubble = ({
  message,
  onArtifactClick,
  isStreaming = false,
  suggestions = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const { role, content, timestamp, metadata, artifacts } = message;
  const isUser = role === 'user';
  const isAgent = role === 'agent';

  // Parse proposal tokens from agent content (only agent messages can have them)
  const proposalTokens = isAgent ? parseProposals(content || '') : [];

  // Parse import preview token (Phase 3b-3) — takes precedence over individual ProposalCards
  // when an IMPORT_PREVIEW token is present
  const importPreview = isAgent ? parseImportPreview(content || '') : null;

  if (isAgent) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          mb: 2,
          gap: 1
        }}
      >
        {/* Agent avatar */}
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: 'primary.main',
            flexShrink: 0
          }}
        >
          <SmartToyIcon sx={{ fontSize: '1rem' }} />
        </Avatar>

        {/* Agent bubble */}
        <Box sx={{ maxWidth: '80%' }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontWeight: 500 }}
          >
            Travel Agent
          </Typography>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              color: '#222',
              padding: '10px 14px',
              borderRadius: '4px 18px 18px 18px',
              wordBreak: 'break-word',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              lineHeight: 1.6
            }}
          >
            {isStreaming ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                <WaitingIndicator size="small" message="" />
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Thinking...
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                component="div"
                sx={{ fontSize: '0.9375rem', lineHeight: 1.6 }}
              >
                {renderContent(content, onArtifactClick, artifacts)}
              </Typography>
            )}
          </Box>

          {/* Timestamp + tools used */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(timestamp)}
            </Typography>
            {metadata?.toolsUsed?.length > 0 && (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                (used: {metadata.toolsUsed.join(', ')})
              </Typography>
            )}
          </Box>

          {/* Import Preview card — batch mode for Guided Import (Phase 3b-3)
              Shown when message contains [[IMPORT_PREVIEW:batchId:title:count]] token.
              Takes precedence over individual ProposalCards for the same proposals. */}
          {!isStreaming && importPreview && (
            <ImportPreviewCard
              batchId={importPreview.batchId}
              title={importPreview.title}
              itemCount={importPreview.count}
              suggestions={suggestions}
              onApprove={onApprove}
              onDismiss={onDismiss}
              onModify={onModify}
              canApprove={canApprove}
            />
          )}

          {/* Individual proposal cards — one per [[PROPOSAL:...]] token found in this message.
              Skipped when an IMPORT_PREVIEW card is shown (it handles all batch proposals). */}
          {!isStreaming && !importPreview && proposalTokens.length > 0 && proposalTokens.map((token) => {
            const suggestion = Array.isArray(suggestions)
              ? suggestions.find((s) => s.refId === token.refId)
              : undefined;
            return (
              <ProposalCard
                key={token.refId}
                suggestion={suggestion || null}
                onApprove={onApprove}
                onDismiss={onDismiss}
                onModify={onModify}
                canApprove={canApprove}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // User message
  if (isUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'flex-start',
          mb: 2,
          gap: 1
        }}
      >
        {/* User bubble */}
        <Box sx={{ maxWidth: '80%' }}>
          <Box
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              padding: '10px 14px',
              borderRadius: '18px 18px 4px 18px',
              wordBreak: 'break-word',
              boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
            }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={{ fontSize: '0.9375rem', lineHeight: 1.6 }}
            >
              {renderContent(content, onArtifactClick, artifacts)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)' }}>
              {formatTime(timestamp)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default MessageBubble;
